import {
    DAILY,
    WEEKLY,
    MONTHLY,
    YEARLY,
    RECURRENCE_DATE_TIME_FORMAT,
    DaysOfWeek,
    MonthsMap,
} from "constant";
import EventService from "service/eventService";
import { isEmpty } from "lodash";
import {
    ConditionPatchType,
    ConditionPostType,
    EventCondtionGateway,
    EventCondtionLdsu,
    EventCondtionSensor,
    EventCondtionVariable,
} from "generated/models/Event";

interface RecurrencePayloadOptions {
    slot: number;
    selectedRepeatOption: string;
    repeatIntervalVal: any;
    recurrenceRange: any;
    selectedTimeOption?: any;
    priTime?: any;
    secTime?: any;
    timeList?: any;
    timeRangeList?: any;
    duration?: any;
    checkboxState?: any;
    allDates: any[];
}

interface Date1Resource {
    day?: string;
    time_: {};
}

interface Date2Resource {
    start: Date;
    end?: Date;
    repeat: string;
    interval: number;
    [DAILY]: {};
    [WEEKLY]: {};
    [MONTHLY]: {};
    [YEARLY]: {};
}

export interface ConditionPayload {
    slot: number;
    type_: ConditionType;
    resource: {
        date1?: Date1Resource;
        date2?: Date2Resource;
        device?:
            | EventCondtionGateway
            | EventCondtionLdsu
            | EventCondtionSensor
            | EventCondtionVariable;
    };
}
export type ConditionType = ConditionPatchType | ConditionPostType;

const buildBasePayload = (
    slot: number,
    selectedRepeatOption: string,
    recurrenceRange: any,
    type_: ConditionType = "DATETIME"
) => {
    const range = recurrenceRange[selectedRepeatOption];

    return {
        slot,
        type_,
        resource: {
            date2: {
                start: range.startDate.format(RECURRENCE_DATE_TIME_FORMAT),
                ...(range.endDateEnabled && {
                    end: range.endDate.format(RECURRENCE_DATE_TIME_FORMAT),
                }),
                repeat: "",
                interval: 0,
                [DAILY]: {},
                [WEEKLY]: {},
                [MONTHLY]: {},
                [YEARLY]: {},
            },
        },
    };
};

export const buildDailyPayload = (
    options: RecurrencePayloadOptions
): ConditionPayload => {
    const {
        slot,
        repeatIntervalVal,
        recurrenceRange,
        selectedRepeatOption,
        selectedTimeOption,
        priTime,
        secTime,
        timeList,
        timeRangeList,
        duration,
    } = options;

    const body = buildBasePayload(slot, selectedRepeatOption, recurrenceRange);
    const date2 = body.resource.date2;

    date2.repeat = DAILY;
    date2.interval = Number(repeatIntervalVal[DAILY].value);
    date2[DAILY] = EventService.getEventTimeRange(
        selectedTimeOption.default,
        priTime.default,
        secTime.default,
        timeList.default,
        timeRangeList.default,
        duration.default
    );

    delete date2[WEEKLY];
    delete date2[MONTHLY];
    delete date2[YEARLY];

    return body;
};

export const buildWeeklyPayload = (
    options: RecurrencePayloadOptions
): ConditionPayload | Error => {
    const {
        slot,
        repeatIntervalVal,
        recurrenceRange,
        selectedRepeatOption,
        selectedTimeOption,
        priTime,
        secTime,
        timeList,
        timeRangeList,
        duration,
        checkboxState,
    } = options;

    const body = buildBasePayload(slot, selectedRepeatOption, recurrenceRange);
    const date2 = body.resource.date2;

    date2.repeat = WEEKLY;
    date2.interval = Number(repeatIntervalVal[WEEKLY].value);

    const weekdayObj: any = {};
    for (const day of DaysOfWeek) {
        if (checkboxState[day]) {
            weekdayObj[DaysOfWeek.indexOf(day) + 1] =
                EventService.getEventTimeRange(
                    selectedTimeOption[day],
                    priTime[day],
                    secTime[day],
                    timeList[day],
                    timeRangeList[day],
                    duration[day]
                );
        }
    }

    date2[WEEKLY] = { weekdays: weekdayObj };

    delete date2[DAILY];
    delete date2[MONTHLY];
    delete date2[YEARLY];

    return body;
};

export const buildMonthlyPayload = (
    options: RecurrencePayloadOptions
): ConditionPayload | Error => {
    const {
        slot,
        repeatIntervalVal,
        recurrenceRange,
        selectedRepeatOption,
        allDates,
    } = options;

    const monthlyDates = allDates.filter((m: any) =>
        m.type.includes("monthly")
    );

    if (!monthlyDates.length) {
        return new Error("Please select a recurring day.");
    }

    const body = buildBasePayload(slot, selectedRepeatOption, recurrenceRange);
    const date2 = body.resource.date2;

    date2.repeat = MONTHLY;
    date2.interval = Number(repeatIntervalVal[MONTHLY].value);

    const monthlyWeekdayObj: any = {};
    const monthlyDayObj: any = {};

    for (const d of monthlyDates) {
        const timeRange = EventService.getEventTimeRange(
            d.selectedTimeOption,
            d.priTime,
            d.secTime,
            d.timeList,
            d.timeRangeList,
            d.duration
        );

        if (d.type === "monthly_recurrence:day") {
            monthlyDayObj[d.day] = timeRange;
        } else {
            monthlyWeekdayObj[d.day] = timeRange;
        }
    }

    date2[MONTHLY] = {
        ...(isEmpty(monthlyWeekdayObj)
            ? undefined
            : { weekdays: monthlyWeekdayObj }),
        ...(isEmpty(monthlyDayObj) ? undefined : { days: monthlyDayObj }),
    };

    delete date2[DAILY];
    delete date2[WEEKLY];
    delete date2[YEARLY];

    return body;
};

export const buildYearlyPayload = (
    options: RecurrencePayloadOptions
): ConditionPayload | Error => {
    const {
        slot,
        repeatIntervalVal,
        recurrenceRange,
        selectedRepeatOption,
        allDates,
    } = options;

    const yearlyDates = allDates.filter((m: any) => m.type.includes("yearly"));

    if (!yearlyDates.length) {
        return new Error("Please select a recurring day.");
    }

    const body = buildBasePayload(slot, selectedRepeatOption, recurrenceRange);
    const date2 = body.resource.date2;

    date2.repeat = YEARLY;
    date2.interval = Number(repeatIntervalVal[YEARLY].value);

    const yearlyMonthObj: any = {};

    for (const d of yearlyDates) {
        const monthlyVal: any = Object.keys(MonthsMap).find(
            (key: any) => MonthsMap[key].toLowerCase() === d.month
        );

        const timeRange = EventService.getEventTimeRange(
            d.selectedTimeOption,
            d.priTime,
            d.secTime,
            d.timeList,
            d.timeRangeList,
            d.duration
        );

        const fieldName =
            d.type === "yearly_recurrence:day" ? "days" : "weekdays";

        yearlyMonthObj[monthlyVal] = {
            ...yearlyMonthObj[monthlyVal],
            [fieldName]: {
                ...yearlyMonthObj[monthlyVal]?.[fieldName],
                [d.day]: timeRange,
            },
        };
    }

    date2[YEARLY] = { months: yearlyMonthObj };

    delete date2[DAILY];
    delete date2[WEEKLY];
    delete date2[MONTHLY];

    return body;
};

/**
 * Main function to build payload based on recurrence type
 */
export const buildRecurrencePayload = (
    selectedRepeatOption: string,
    options: RecurrencePayloadOptions
): ConditionPayload | Error => {
    switch (selectedRepeatOption) {
        case DAILY:
            return buildDailyPayload(options);
        case WEEKLY:
            return buildWeeklyPayload(options);
        case MONTHLY:
            return buildMonthlyPayload(options);
        case YEARLY:
            return buildYearlyPayload(options);
        default:
            throw new Error(`Unknown recurrence type: ${selectedRepeatOption}`);
    }
};
